import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";

/**
 * One figure on the Performance page, with the rule that produced it.
 *
 * `explanation` is required, not optional. A carrier who cannot reproduce a
 * number from their own records assumes it is wrong, and every metric here has
 * a denominator or a cut-off that isn't guessable from the label — "Win Rate"
 * is over shipments quoted, "On-Time" is against the late end of a transit
 * window, "Cheapest" is per quote board. Making it required means a new stat
 * cannot be added without saying what it means.
 *
 * Wording must match the [Metric Glossary] where a term is defined there, so a
 * carrier and an operator reading the same word mean the same thing.
 *
 * [Metric Glossary]: https://handbook.freightsimple.com/key-concepts/metric-glossary
 */
export function PerformanceMetric({
  label,
  value,
  hint,
  explanation,
  size = "2xl",
}: {
  label: string;
  value: string;
  /** The counts behind the figure, e.g. "3 of 41 you priced". Omit when there are none. */
  hint?: string | undefined;
  explanation: string;
  /** Headline figures run larger than the ones packed into a dense grid. */
  size?: "xl" | "2xl";
}) {
  return (
    <Stack gap={1}>
      <div className="flex items-center gap-1.5">
        <T size="sm" color="secondary">
          {label}
        </T>
        <Tooltip variant="info" size="sm" content={explanation} />
      </div>
      <T size={size} weight="semibold">
        {value}
      </T>
      {hint !== undefined && hint !== "" && (
        <T size="xs" color="muted">
          {hint}
        </T>
      )}
    </Stack>
  );
}
